import { StringUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";

/**
 * The image text element render bitmap font to text.
 * Example:
 *   <TextElement Class="TextElement" Font="fontName" Text="09123456789.,+" X="960" Y="540" TextAlign="center" TextBaseline="middle" />
 *
 * @class TextElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class TextElement extends PIXI.Text {
    private _uid: string;
    private _cloned: boolean;
    private __destroyed: boolean;

    constructor() {
        super();

        this.text = "";

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element) {
        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute(TextElementAttributes.FONT)) {
            this.style.fontFamily = node.getAttribute(TextElementAttributes.FONT);
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        if (node.hasAttribute(TextElementAttributes.TEXT)) {
            this.text = node.getAttribute(TextElementAttributes.TEXT);
        }

        if (node.hasAttribute(TextElementAttributes.TEXT_ALIGN)) {
            //this.style.align = node.getAttribute(TextElementAttributes.TEXT_ALIGN);
        }

        if (node.hasAttribute(TextElementAttributes.TEXT_BASELINE)) {
            //this.textBaseline = node.getAttribute(TextElementAttributes.TEXT_BASELINE);
            /*switch (node.getAttribute(TextElementAttributes.TEXT_BASELINE)) {
                case TextElementAttributes.TEXT_BASELINE_VALUE.MIDDLE:
                    this.position.y = this.position.y - this.parent.height / 2;
                    break;
                case TextElementAttributes.TEXT_BASELINE_VALUE.BOTTOM:
                    this.position.y = -this.parent.height;
                    break;
            }*/
        }

        if (node.hasAttribute(TextElementAttributes.SIZE)) {
            this.style.fontSize = parseInt(node.getAttribute(TextElementAttributes.SIZE));
        }

        if (node.hasAttribute(TextElementAttributes.ALPHA)) {
            this.alpha = parseFloat(node.getAttribute(TextElementAttributes.ALPHA));
        }

        if (node.hasAttribute(TextElementAttributes.ROTATION)) {
            this.rotation = parseInt(node.getAttribute(TextElementAttributes.ROTATION));
        }

        if (node.hasAttribute(TextElementAttributes.START_ROTATION)) {
            if (node.getAttribute(TextElementAttributes.START_ROTATION) === TextElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        if (node.hasAttribute(TextElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(TextElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(TextElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(TextElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(TextElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(TextElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(TextElementAttributes.SCALE));
        }

        if (node.hasAttribute(TextElementAttributes.X)) {
            this.position.x = parseInt(node.getAttribute(TextElementAttributes.X));
        }

        if (node.hasAttribute(TextElementAttributes.Y)) {
            this.position.y = parseInt(node.getAttribute(TextElementAttributes.Y));
        }

        if (node.hasAttribute(TextElementAttributes.PIVOT_X)) {
            this.pivot.x = parseInt(node.getAttribute(TextElementAttributes.PIVOT_X));
        }

        if (node.hasAttribute(TextElementAttributes.PIVOT_Y)) {
            this.pivot.y = parseInt(node.getAttribute(TextElementAttributes.PIVOT_Y));
        }

        if (node.hasAttribute(TextElementAttributes.VISIBLE)) {
            this.visible = StringUtils.parseBoolean(node.getAttribute(TextElementAttributes.VISIBLE));
        }

        if (node.hasAttribute(TextElementAttributes.TINT)) {
            if (node.getAttribute(TextElementAttributes.TINT) === "0") {
                this.style.fill = null;
            } else {
                this.style.fill = parseInt(node.getAttribute(TextElementAttributes.TINT), 16);
            }
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    /**
     * Marks element as cloned
     */
    markAsCloned(): void {
        this._markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    protected _markAsCloned(): void {
        this.assertDestroyed("markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);

                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    getDebugInfo(): string {
        if (DEBUG) {
            let debugInfoFn = (this as any).debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }
        return "";
    }

    debugInfo(): string {
        if (DEBUG) {
            let result = `${this.uid}\nName: [${this.name}]`;

            return result;
        }
        return "";
    }
}

/**
 *
 * @type {{DURATION: string; ROUND: string; FORCE: string}}
 */
export const TextElementAttributes = {
    /**
     * The text to display.
     * @property Text
     * @type string
     * @default null
     */
    TEXT: "Text",
    /*
     * Bitmap font for text.
     * @property Font
     * @type string
     * @default null
     */
    FONT: "Font",
    /**
     * The font size (as a number it converts to px, but as a string, equivalents are '26px','20pt','160%' or '1.6em')
     */
    SIZE: "Size",
    /*
     * The horizontal text alignment. Any of "left", "right", and "center".
     * For detailed information view the whatwg spec. Default is "left".
     * @property TextAlign
     * @type string
     * @default left
     */
    TEXT_ALIGN: "TextAlign",
    TEXT_ALIGN_VALUE: {
        LEFT: "left",
        CENTER: "center",
        RIGHT: "right",
    },
    /*
     * The vertical alignment point on the text.
     * Any of "top", "middle" or "bottom". Default is "top".
     * @property TextBaseline
     * @type string
     * @default top
     */
    TEXT_BASELINE: "TextBaseline",
    TEXT_BASELINE_VALUE: {
        MIDDLE: "middle",
        BOTTOM: "bottom",
    },
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Texts/TextElement.ts
 **/